import { useQuery } from "@tanstack/react-query";
import { format, subDays } from "date-fns";

import { apiGet, mockFor, mockResponse, queryKey } from "./client";
import { officesFingerprint, officesSettled, useOfficeMapping, type OfficeMapping } from "./office-mapping";
import type { HealthSummary, OfficeComparisonRow, SparkPoint } from "./types";

function sparkline(points: number, start: number, end: number, jitter = 0.35): SparkPoint[] {
  return Array.from({ length: points }, (_, i) => {
    const t = points === 1 ? 1 : i / (points - 1);
    const wobble = Math.sin(i * 1.7) * jitter;
    return {
      date: format(subDays(new Date(), points - 1 - i), "yyyy-MM-dd"),
      value: Number((start + (end - start) * t + wobble).toFixed(2)),
    };
  });
}

/** Deterministic pre-Athena baselines keyed off the office list order. */
const BASELINES: Record<string, { first: number; final: number; capacity: number }> = {
  Dallas: { first: 13.8, final: 27.4, capacity: 11.2 },
  Atlanta: { first: 11.6, final: 24.1, capacity: 12.4 },
  Chicago: { first: 15.2, final: 31.7, capacity: 9.8 },
  Phoenix: { first: 9.4, final: 19.8, capacity: 13.1 },
  Charlotte: { first: 17.5, final: 35.2, capacity: 8.6 },
};

function buildOfficeComparison(offices: OfficeMapping[]): OfficeComparisonRow[] {
  const rows = offices.map((office, index) => {
    const base =
      BASELINES[office.officeName] ??
      { first: 12 + index, final: 25 + index * 2, capacity: 10 + index * 0.4 };

    return {
      officeName: office.officeName,
      liveOnAthena: office.liveOnAthena,
      firstDecisionTimePreHours: base.first,
      firstDecisionTimeCurrentHours: office.liveOnAthena ? 4.2 : null,
      finalDecisionTimePreHours: base.final,
      finalDecisionTimeCurrentHours: office.liveOnAthena ? 9.6 : null,
      capacityRatioPre: base.capacity,
      capacityRatioCurrent: office.liveOnAthena ? 19.4 : null,
    } satisfies OfficeComparisonRow;
  });

  return rows.sort((a, b) => a.firstDecisionTimePreHours - b.firstDecisionTimePreHours);
}

function generateHealthSummary(offices: OfficeMapping[]): HealthSummary {
  return {
    applicationsDecided: {
      total: 1482,
      approved: 921,
      funded: 704,
      rejected: 561,
      qtdTotal: 3967,
    },
    avgFirstDecisionTimeHours: {
      current: 4.2,
      previousPeriod: 6.1,
      sparkline: sparkline(14, 6.4, 4.2, 0.3),
    },
    avgFinalDecisionTimeHours: {
      current: 9.6,
      previousPeriod: 12.4,
      sparkline: sparkline(14, 12.8, 9.6, 0.5),
    },
    exceptionsCount: {
      pg: 148,
      corp: 96,
      sparkline: sparkline(14, 12, 21, 2.2),
    },
    overrideRate: {
      current: 8.4,
      previousPeriod: 11.2,
      sparkline: sparkline(14, 11.6, 8.4, 0.6),
    },
    modelHealth: {
      status: "green",
      label: "Athena's scoring model is operating within expected parameters.",
    },
    officeComparison: buildOfficeComparison(offices),
  };
}

export async function fetchHealthSummary(
  offices: OfficeMapping[],
  params: { range?: string; office?: string; compare?: boolean } = {},
): Promise<HealthSummary> {
  if (mockFor("/health-summary")) return mockResponse(generateHealthSummary(offices));
  return apiGet<HealthSummary>("/health-summary", {
    range: params.range,
    office: params.office,
    compare: params.compare ? "true" : undefined,
  });
}

export function useHealthSummary(params: { range?: string; office?: string; compare?: boolean } = {}) {
  const { data: offices, isError: officesFailed } = useOfficeMapping();

  return useQuery({
    queryKey: queryKey("health-summary", { ...params, offices: officesFingerprint(offices) }),
    queryFn: () => fetchHealthSummary(offices ?? [], params),
    enabled: officesSettled(offices, officesFailed),
    staleTime: 60 * 1000,
  });
}
